/**
 * Uma camada que tapa o ecrã inteiro e tira o resto da página do alcance: o ecrã de bloqueio
 * por inactividade e o aviso de sessão perdida. Não fecha com Esc nem ao tocar fora.
 *
 * Camadas: fica acima das janelas (`z-[200]`) e dos menus (`z-[300]`) do kit, e abaixo do
 * SweetAlert (`z-index` 1060), para que um `Pops` aberto daqui (palavra-passe errada) apareça
 * por cima dela e com o botão vivo.
 *
 * Enquanto está aberta:
 * - o resto do `body` fica `inert` (nem rato, nem teclado, nem leitor de ecrã) — menos um
 *   SweetAlert que já esteja aberto, para não ficar com o «OK» morto;
 * - a página não desliza;
 * - as teclas não saem daqui: nem o ⌘K/⌘B do casco nem o Esc de uma janela por baixo.
 *
 * Usada também na app do POS (o `ModalAutenticacao`): só depende do Tailwind do `app.css`.
 */
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export function CamadaExclusiva({ aberta, idTitulo, children, className }: {
    aberta: boolean;
    /** O `id` do título, para os leitores de ecrã. */
    idTitulo?: string;
    children: React.ReactNode;
    className?: string;
}) {
    const camada = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!aberta) {
            return;
        }
        const html = document.documentElement;
        const overflowAntes = html.style.overflow;
        html.style.overflow = "hidden";

        const tirados: Element[] = [];
        Array.from(document.body.children).forEach((el) => {
            if (el === camada.current || el.hasAttribute("inert") || el.classList.contains("swal2-container") || el.tagName === "SCRIPT") {
                return;
            }
            el.setAttribute("inert", "");
            tirados.push(el);
        });

        return () => {
            html.style.overflow = overflowAntes;
            tirados.forEach((el) => el.removeAttribute("inert"));
        };
    }, [aberta]);

    if (!aberta || typeof document === "undefined") {
        return null;
    }

    return createPortal(
        <div
            ref={camada}
            data-sobreposicao-logigate
            role="dialog"
            aria-modal="true"
            aria-labelledby={idTitulo}
            onKeyDown={(e) => e.stopPropagation()}
            className={cn("scroll-bonito fixed inset-0 z-[1000] overflow-y-auto overscroll-contain bg-zinc-950/60 backdrop-blur-xl animate-in fade-in-0", className)}
        >
            <div className="flex min-h-full items-center justify-center px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))]">
                {children}
            </div>
        </div>,
        document.body,
    );
}
